import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import css from './Step2Contact.module.css';

const Step2Contact = ({ nextStep, prevStep, initialValues, className, rootClassName }) => {
  const classes = classNames(rootClassName || css.root, className);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validate = values => {
    const errors = {};
    if (!values.contactPersonName?.trim()) errors.contactPersonName = 'Contact name is required';
    if (!values.designation?.trim()) errors.designation = 'Designation is required';
    if (!values.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (!values.phone?.trim()) errors.phone = 'Phone number is required';
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 8) {
      errors.password = 'Minimum 8 characters';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const onSubmit = values => {
    const { confirmPassword, ...rest } = values;
    nextStep(rest);
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classNames(classes, css.form)}>
          <div className={css.header}>
            <span className={css.step}>Step 2 of 5</span>
            <h2 className={css.title}>Contact Person Information</h2>
            <p className={css.description}>Please provide the contact person details.</p>
          </div>

          <hr className={css.divider} />

          <section className={css.section}>
            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Contact Person Name <span className={css.required}>*</span>
                </label>
                <Field name="contactPersonName">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter full name"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Position / Designation <span className={css.required}>*</span>
                </label>
                <Field name="designation">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter designation"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Email Address <span className={css.required}>*</span>
                </label>
                <Field name="email">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        type="email"
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter email address"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Phone Number <span className={css.required}>*</span>
                </label>
                <Field name="phone">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter phone number"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>
            <div className={css.flex}>
              {/* WhatsApp */}

              <div className={css.field}>
                <label className={css.label}>WhatsApp Number</label>

                <Field name="whatsapp">
                  {({ input }) => (
                    <input {...input} className={css.input} placeholder="Enter WhatsApp number" />
                  )}
                </Field>
              </div>

              {/* WeChat */}

              <div className={css.field}>
                <label className={css.label}>WeChat ID</label>

                <Field name="wechat">
                  {({ input }) => (
                    <input {...input} className={css.input} placeholder="Enter WeChat ID" />
                  )}
                </Field>
              </div>
            </div>

            {/* Alternative Email */}

            <div className={css.fieldFull}>
              <label className={css.label}>Alternative Email (Optional)</label>

              <Field name="alternativeEmail">
                {({ input }) => (
                  <input
                    {...input}
                    type="email"
                    className={css.input}
                    placeholder="Enter alternative email"
                  />
                )}
              </Field>
            </div>

            {/* Account Password */}

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Account Password
                  <span className={css.required}>*</span>
                </label>
                <Field name="password">
                  {({ input, meta }) => (
                    <>
                      <div className={css.passwordBox}>
                        <input
                          {...input}
                          type={showPassword ? 'text' : 'password'}
                          className={css.input}
                          placeholder="Min. 8 characters"
                        />
                        <button
                          type="button"
                          className={css.eyeBtn}
                          onClick={() => setShowPassword(v => !v)}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && (
                        <span className={css.error}>{meta.error}</span>
                      )}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Confirm Password
                  <span className={css.required}>*</span>
                </label>
                <Field name="confirmPassword">
                  {({ input, meta }) => (
                    <>
                      <div className={css.passwordBox}>
                        <input
                          {...input}
                          type={showConfirm ? 'text' : 'password'}
                          className={css.input}
                          placeholder="Re-enter password"
                        />
                        <button
                          type="button"
                          className={css.eyeBtn}
                          onClick={() => setShowConfirm(v => !v)}
                        >
                          {showConfirm ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && (
                        <span className={css.error}>{meta.error}</span>
                      )}
                    </>
                  )}
                </Field>
              </div>
            </div>
          </section>

          <div className={css.buttonContainer}>
            <button type="button" onClick={prevStep} className={css.previous}>
              ← Previous
            </button>

            <button type="submit" className={css.button}>
              Next : Industry Details →
            </button>
          </div>
        </form>
      )}
    />
  );
};

Step2Contact.defaultProps = {
  className: null,
  rootClassName: null,
};

Step2Contact.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default Step2Contact;
